import type { Step, StepContent } from '@/components/stepsCarousel';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

type UseStepsCarouselArgs = {
  steps: Step[];
  intervalMs?: number;
  initialStep?: number;
};

/**
 * Manages the state and behaviour for an auto-advancing steps carousel.
 *
 * This hook encapsulates all carousel logic, including:
 * - tracking the currently active step
 * - deriving step display content and associated assets
 * - automatically advancing steps on an interval
 * - pausing and resuming playback
 * - handling manual step selection
 * - ensuring intervals are safely cleaned up on unmount
 *
 * The hook assumes that the `steps` array is stable for the lifetime
 * of the component (i.e. its length does not change after mount).
 *
 * Typical usage:
 * - Call `play` when the carousel enters view
 * - Call `pause` or `reset` when it exits view
 * - Use `onStepClick` to allow manual navigation that resumes autoplay
 *
 * @param steps - Ordered list of steps used to build the carousel.
 * @param intervalMs - Time in milliseconds between automatic step transitions (default: 3000).
 * @param initialStep - Step index to reset to when the carousel is reset (default: 0).
 *
 * @returns An object containing:
 * - `activeStep`: The currently active step index.
 * - `setActiveStep`: Setter for manually controlling the active step.
 * - `stepsContent`: Display-ready step metadata (number and text).
 * - `stepsAssets`: Array of step image assets aligned by index.
 * - `play`: Starts the auto-advance interval if not already running.
 * - `pause`: Stops the auto-advance interval.
 * - `reset`: Stops playback and resets the active step.
 * - `onStepClick`: Handler for manual step selection that resumes playback.
 */

export function useStepsCarousel({ steps, intervalMs = 3000, initialStep = 0 }: UseStepsCarouselArgs) {
  const [activeStep, setActiveStep] = useState<number>(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stepsContent: StepContent[] = useMemo(
    () =>
      steps.map(({ stepNumber, stepText }) => ({
        stepNumber,
        stepText,
      })),
    [steps],
  );

  const stepsAssets: string[] = useMemo(() => steps.map((s) => s.stepImage), [steps]);

  const lastStepIndex = stepsContent.length - 1;

  const pause = useCallback(() => {
    if (!intervalRef.current) {
      return;
    }
    clearInterval(intervalRef.current);
    intervalRef.current = null;
  }, []);

  const resetActiveStep = useCallback(() => {
    setActiveStep(initialStep);
  }, [initialStep]);

  const play = useCallback(() => {
    if (intervalRef.current) {
      return;
    }

    intervalRef.current = setInterval(() => {
      setActiveStep((prev) => (prev === lastStepIndex ? 0 : prev + 1));
    }, intervalMs);
  }, [intervalMs, lastStepIndex]);

  const reset = useCallback(() => {
    pause();
    resetActiveStep();
  }, [pause, resetActiveStep]);

  const onStepClick = useCallback(
    (stepIndex: number) => {
      pause();
      setActiveStep(stepIndex);
      play();
    },
    [pause, play],
  );

  useEffect(() => pause, [pause]);

  return {
    activeStep,
    setActiveStep, // optional, but handy
    stepsContent,
    stepsAssets,
    play,
    pause,
    reset,
    onStepClick,
  };
}
